import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import {
  IconAlertTriangle,
  IconCheck,
  IconMail,
  IconX,
} from '@tabler/icons-react';
import axios from 'axios';
import classes from './Sidebar.module.css';

interface Location {
  id: number;
  label: string;
  click_house_ids: string[];
  has_energy_meter: boolean;
}

interface SendResult {
  sent: string[];
  no_data: string[];
  failed: string[];
}

interface FormValues {
  totemId: string | null;
  clickHouseIds: string;
  year: string;
  month: string;
  email: string;
}

const MONTHS = [
  { value: '1', label: 'Siječanj' },
  { value: '2', label: 'Veljača' },
  { value: '3', label: 'Ožujak' },
  { value: '4', label: 'Travanj' },
  { value: '5', label: 'Svibanj' },
  { value: '6', label: 'Lipanj' },
  { value: '7', label: 'Srpanj' },
  { value: '8', label: 'Kolovoz' },
  { value: '9', label: 'Rujan' },
  { value: '10', label: 'Listopad' },
  { value: '11', label: 'Studeni' },
  { value: '12', label: 'Prosinac' },
];

/** The previous calendar month, used to pre-fill the period fields. */
function previousMonth(): { year: number; month: number } {
  const now = new Date();
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDayOfPrevMonth = new Date(firstOfMonth.getTime() - 24 * 60 * 60 * 1000);
  return { year: lastDayOfPrevMonth.getFullYear(), month: lastDayOfPrevMonth.getMonth() + 1 };
}

const FIELD_BY_API_NAME: { [apiName: string]: keyof FormValues } = {
  totem_id: 'totemId',
  click_house_ids: 'clickHouseIds',
  year: 'year',
  month: 'month',
  email: 'email',
};

export default function EnergyReportModal() {
  const [opened, { open, close }] = useDisclosure(false);
  const [locations, setLocations] = useState<Location[] | null>(null);
  const [onlyWithMeter, setOnlyWithMeter] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<SendResult | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const { year, month } = previousMonth();
  const form = useForm<FormValues>({
    initialValues: {
      totemId: null,
      clickHouseIds: '',
      year: String(year),
      month: String(month),
      email: '',
    },
  });

  useEffect(() => {
    if (opened && locations === null) {
      axios
        .get('/admin/postbuy/energy-report/locations/')
        .then((response) => setLocations(response.data.locations))
        .catch((error) => {
          console.error('Failed to load locations for the energy report popup', error);
          setLocations([]);
        });
    }
  }, [opened, locations]);

  const locationOptions = (locations || [])
    .filter((location) => !onlyWithMeter || location.has_energy_meter)
    .map((location) => ({
      value: String(location.id),
      label: `${location.label} — ${location.click_house_ids.join(', ')}`,
    }));

  const handleClose = () => {
    close();
    form.reset();
    setResult(null);
    setFormError(null);
  };

  const handleSubmit = form.onSubmit(async (values) => {
    setSubmitting(true);
    setResult(null);
    setFormError(null);

    try {
      const response = await axios.post('/admin/postbuy/energy-report/send/', {
        totem_id: values.totemId ? Number(values.totemId) : null,
        click_house_ids: values.clickHouseIds,
        year: Number(values.year),
        month: Number(values.month),
        email: values.email,
      });
      setResult(response.data);
    } catch (error: any) {
      const errors = error?.response?.data?.errors;
      if (!errors) {
        setFormError('Slanje nije uspjelo. Provjerite konzolu za detalje.');
        console.error('Energy report send failed', error);
        return;
      }
      Object.entries(errors).forEach(([apiName, messages]) => {
        const text = ([] as string[]).concat(messages as any).join(' ');
        const fieldName = FIELD_BY_API_NAME[apiName];
        if (fieldName) {
          form.setFieldError(fieldName, text);
        } else {
          setFormError(text);
        }
      });
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <>
      <UnstyledButton onClick={open} className={classes.control}>
        <Group className={classes.row}>
          <IconMail className={classes.icon} />
          <Text>Pošalji izvještaj o brojilu</Text>
        </Group>
      </UnstyledButton>

      <Modal opened={opened} onClose={handleClose} title="Pošalji izvještaj o brojilu" centered>
        <form onSubmit={handleSubmit}>
          <Stack gap="sm">
            <Select
              label="Lokacija"
              placeholder={locations === null ? 'Učitavanje...' : 'Odaberite lokaciju'}
              data={locationOptions}
              searchable
              clearable
              disabled={locations === null}
              {...form.getInputProps('totemId')}
            />
            <Checkbox
              label="Samo lokacije s brojilom"
              checked={onlyWithMeter}
              onChange={(event) => setOnlyWithMeter(event.currentTarget.checked)}
            />
            <TextInput
              label="ClickHouse ID"
              placeholder="cid30p1, cid30p2"
              description="Ako se unese, ima prednost pred ID-evima odabrane lokacije."
              {...form.getInputProps('clickHouseIds')}
            />
            <Group grow>
              <Select
                label="Mjesec"
                data={MONTHS}
                allowDeselect={false}
                {...form.getInputProps('month')}
              />
              <TextInput
                label="Godina"
                type="number"
                required
                min={1}
                max={new Date().getFullYear() + 1}
                {...form.getInputProps('year')}
              />
            </Group>
            <TextInput
              label="E-mail primatelja"
              placeholder="ime@example.com, ime2@example.com"
              description="Više adresa odvojite zarezom."
              required
              {...form.getInputProps('email')}
            />

            {formError && (
              <Alert color="red" icon={<IconX size={18} />}>
                {formError}
              </Alert>
            )}

            {result && (
              <Stack gap="xs">
                {result.sent.length > 0 && (
                  <Alert color="green" icon={<IconCheck size={18} />} title="Poslano">
                    {result.sent.join(', ')}
                  </Alert>
                )}
                {result.no_data.length > 0 && (
                  <Alert
                    color="yellow"
                    icon={<IconAlertTriangle size={18} />}
                    title="Nema podataka za odabrani mjesec"
                  >
                    {result.no_data.join(', ')}
                  </Alert>
                )}
                {result.failed.length > 0 && (
                  <Alert color="red" icon={<IconX size={18} />} title="Nije uspjelo">
                    {result.failed.join(', ')}
                  </Alert>
                )}
              </Stack>
            )}

            <Group justify="flex-end">
              <Button type="submit" disabled={submitting}>
                {submitting ? <Loader size="xs" color="white" /> : 'Pošalji'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
}
